import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';
import { validateTemplateName } from '@/lib/shifts/template-utils';

interface BaseTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description?: string) => void;
  title: string;
  icon: LucideIcon;
  children: React.ReactNode; // Preview content
  saveButtonText?: string;
  isLoading?: boolean;
}

const BaseTemplateDialog: React.FC<BaseTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  icon: Icon,
  children,
  saveButtonText = 'Save Template',
  isLoading = false,
}) => {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTemplateName('');
      setDescription('');
      setNameError(null);
    }
  }, [isOpen]);

  // Validate name on change
  const handleNameChange = (value: string) => {
    setTemplateName(value);
    setNameError(null); // Clear error on change
  };

  const handleSave = async () => {
    const validation = validateTemplateName(templateName);

    if (!validation.isValid) {
      setNameError(validation.error || 'Invalid template name');
      return;
    }

    try {
      await onSave(templateName.trim(), description.trim() || undefined);
      handleClose();
    } catch (error) {
      // Error handled in parent component - do not close dialog
      console.error('Error saving template:', error);
    }
  };

  const handleClose = () => {
    setTemplateName('');
    setDescription('');
    setNameError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1">
          {/* Template preview */}
          <Card className="border-muted/50">
            <CardContent className="p-3">
              {children}
            </CardContent>
          </Card>

          {/* Form fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter template name"
                className={nameError ? 'border-red-500' : ''}
                disabled={isLoading}
                maxLength={50}
              />
              {nameError && (
                <p className="text-sm text-red-500">{nameError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description (Optional)</Label>
              <Textarea
                id="template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter optional description"
                rows={3}
                disabled={isLoading}
                maxLength={200}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !templateName.trim()}
          >
            {isLoading ? 'Saving...' : saveButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BaseTemplateDialog;